import { c as _c } from "react/compiler-runtime";
import AttributionModelsIcon from '@precis-digital/kurama/icons/AttributionModelsIcon';
import BudgetOptimiserIcon from '@precis-digital/kurama/icons/BudgetOptimiserIcon';
import EnhancedSignalsIcon from '@precis-digital/kurama/icons/EnhancedSignalsIcon';
import ConnectDataSourcesIcon from '@precis-digital/kurama/icons/ConnectDataSourcesIcon';
import DataInsightsIcon from '@precis-digital/kurama/icons/DataInsightsIcon';
import RecommendationsIcon from '@precis-digital/kurama/icons/RecommendationsIcon';
import { type Notification } from 'shared/api/notifications';
import { useTranslation } from 'shared/translations';
import { type DisplayNotification } from './utils';
import { APP_ROUTES } from 'shared/routes';
export interface IndividualConfig {
  presentationType: PresentationType;
  title: (notification: Notification) => string;
  message: (notification: Notification) => string;
}
export interface GroupedBySourceAndSeverityConfig {
  presentationType: PresentationType;
  title: (count: number, source: Notification['source']['type'], severity: Notification['severity']) => string;
  message: (count: number) => string;
}
type PresentationType = 'individual' | 'group_by_source_and_severity';
type NotificationPresentationConfigItem = IndividualConfig | GroupedBySourceAndSeverityConfig;
interface NotificationPresentationConfig {
  href: string | ((notification: DisplayNotification) => string);
  icon: React.ReactElement;
  low: NotificationPresentationConfigItem;
  moderate: NotificationPresentationConfigItem;
  critical: NotificationPresentationConfigItem;
}
export const useNotificationPresentationConfig = () => {
  const $ = _c(33);
  const {
    t
  } = useTranslation("workspace");
  let t0;
  if ($[0] !== t) {
    t0 = baseTranslationKey => ({
      critical: {
        presentationType: "group_by_source_and_severity" as const,
        title: count => t(`${baseTranslationKey}.title` as any, {
          count
        }),
        message: count_0 => t(`${baseTranslationKey}.message` as any, {
          count: count_0
        })
      },
      moderate: {
        presentationType: "group_by_source_and_severity" as const,
        title: count_1 => t(`${baseTranslationKey}.title` as any, {
          count: count_1
        }),
        message: count_2 => t(`${baseTranslationKey}.message` as any, {
          count: count_2
        })
      },
      low: {
        presentationType: "group_by_source_and_severity" as const,
        title: count_3 => t(`${baseTranslationKey}.title` as any, {
          count: count_3
        }),
        message: count_4 => t(`${baseTranslationKey}.message` as any, {
          count: count_4
        })
      }
    });
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const createStandardSeverityConfig = t0;
  let t1;
  if ($[2] !== t) {
    t1 = baseKeyTranslationKey => ({
      critical: {
        presentationType: "group_by_source_and_severity" as const,
        title: count_5 => t(`${baseKeyTranslationKey}.critical.title` as any, {
          count: count_5
        }),
        message: count_6 => t(`${baseKeyTranslationKey}.critical.message` as any, {
          count: count_6
        })
      },
      moderate: {
        presentationType: "group_by_source_and_severity" as const,
        title: count_7 => t(`${baseKeyTranslationKey}.moderate.title` as any, {
          count: count_7
        }),
        message: count_8 => t(`${baseKeyTranslationKey}.moderate.message` as any, {
          count: count_8
        })
      },
      low: {
        presentationType: "group_by_source_and_severity" as const,
        title: count_9 => t(`${baseKeyTranslationKey}.low.title` as any, {
          count: count_9
        }),
        message: count_10 => t(`${baseKeyTranslationKey}.low.message` as any, {
          count: count_10
        })
      }
    });
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const createSeveritySpecificConfig = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DataInsightsIcon />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== createStandardSeverityConfig) {
    t3 = {
      href: APP_ROUTES.connectors.errorPage,
      icon: t2,
      ...createStandardSeverityConfig("notificationDisplayPreferences.connectors")
    };
    $[5] = createStandardSeverityConfig;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <DataInsightsIcon />;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== createStandardSeverityConfig) {
    t5 = {
      href: APP_ROUTES.reportingSolutions.errorPage,
      icon: t4,
      ...createStandardSeverityConfig("notificationDisplayPreferences.reportingSolutions")
    };
    $[8] = createStandardSeverityConfig;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <AttributionModelsIcon />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== createStandardSeverityConfig) {
    t7 = {
      href: APP_ROUTES.attributionModels.errorPage,
      icon: t6,
      ...createStandardSeverityConfig("notificationDisplayPreferences.attributionModels")
    };
    $[11] = createStandardSeverityConfig;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <BudgetOptimiserIcon />;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== createStandardSeverityConfig) {
    t9 = {
      href: APP_ROUTES.budgetOptimiser.errorPage,
      icon: t8,
      ...createStandardSeverityConfig("notificationDisplayPreferences.budgetOptimiser")
    };
    $[14] = createStandardSeverityConfig;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <EnhancedSignalsIcon />;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== createStandardSeverityConfig) {
    t11 = {
      href: APP_ROUTES.enhancedSignals.basePage,
      icon: t10,
      ...createStandardSeverityConfig("notificationDisplayPreferences.enhancedSignals")
    };
    $[17] = createStandardSeverityConfig;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <RecommendationsIcon />;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== createSeveritySpecificConfig) {
    t13 = {
      href: _temp,
      icon: t12,
      ...createSeveritySpecificConfig("notificationDisplayPreferences.recommendations")
    };
    $[20] = createSeveritySpecificConfig;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ConnectDataSourcesIcon />;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== createStandardSeverityConfig) {
    t15 = {
      href: APP_ROUTES.dataSources.disconnected,
      icon: t14,
      ...createStandardSeverityConfig("notificationDisplayPreferences.dataSources")
    };
    $[23] = createStandardSeverityConfig;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t11 || $[26] !== t13 || $[27] !== t15 || $[28] !== t3 || $[29] !== t5 || $[30] !== t7 || $[31] !== t9) {
    t16 = {
      connectors: t3,
      reporting_solutions: t5,
      attribution_models: t7,
      budget_optimiser: t9,
      enhanced_signals: t11,
      recommendations: t13,
      datasources: t15
    };
    $[25] = t11;
    $[26] = t13;
    $[27] = t15;
    $[28] = t3;
    $[29] = t5;
    $[30] = t7;
    $[31] = t9;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  return t16;
};
function _temp(notification) {
  return APP_ROUTES.recommendations.filterBasePage({
    severity: notification.severity ?? "low"
  });
}